import {
  Bell,
  ChevronsLeft,
  Clock3,
  FileText,
  Heart,
  Home,
  LogOut,
  Menu,
  Search,
  Settings,
  UsersRound,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/logo";
import { useAuth } from "@/features/auth/use-auth";
import { useGroups } from "@/features/groups/group-queries";
import { useInvitationNotifications } from "@/features/invitations/invitation-queries";
import { HeaderSearch } from "@/features/search/header-search";
import { cn, initials } from "@/lib/utils";

const primaryLinks = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/search", label: "Search", icon: Search },
  { to: "/app/favorites", label: "Favorites", icon: Heart },
  { to: "/app/recent", label: "Recent", icon: Clock3 },
  { to: "/app/invitations", label: "Invitations", icon: UsersRound },
  { to: "/app/profile", label: "Profile", icon: Settings },
];

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { user, signOut } = useAuth();
  const { data: pendingInvitations = [] } = useInvitationNotifications();
  const {
    data: groups = [],
    isError: groupsError,
    refetch: refetchGroups,
  } = useGroups();

  const sidebar = (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "flex h-16 items-center border-b border-slate-200 px-4",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        <Logo compact={collapsed} />
        {!collapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
          >
            <ChevronsLeft className="size-4" />
          </Button>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Main">
        {primaryLinks.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                "flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-950",
                isActive &&
                  "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/60",
                collapsed && "justify-center px-0",
              )
            }
            title={collapsed ? label : undefined}
          >
            <Icon className="size-[18px] shrink-0" aria-hidden="true" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <div className="pt-6">
            <div className="flex items-center justify-between px-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Groups
              </p>
              <button
                className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700"
                aria-label="Group settings"
              >
                <Settings className="size-3.5" />
              </button>
            </div>
            <div className="mt-2 space-y-1">
              {groupsError ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center">
                  <p className="text-xs text-amber-800">
                    Groups could not be refreshed. Your saved data has not been
                    deleted.
                  </p>
                  <button
                    type="button"
                    className="mt-2 text-xs font-semibold text-amber-900 underline"
                    onClick={() => void refetchGroups()}
                  >
                    Retry
                  </button>
                </div>
              ) : groups.length ? (
                groups
                  .filter(({ group }) => group.status === "active")
                  .slice(0, 8)
                  .map(({ group }) => (
                    <NavLink
                      key={group.id}
                      to={`/app/groups/${group.id}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-white hover:text-slate-950",
                          isActive && "bg-white font-semibold text-indigo-700",
                        )
                      }
                    >
                      <span>{group.icon}</span>
                      <span className="truncate">{group.name}</span>
                    </NavLink>
                  ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 p-3 text-center">
                  <FileText className="mx-auto size-4 text-slate-400" />
                  <p className="mt-1 text-xs text-slate-500">
                    Your groups will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <div className="border-t border-slate-200 p-3">
        <div
          className={cn(
            "flex items-center gap-3 rounded-xl p-2",
            collapsed && "justify-center",
          )}
        >
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt=""
              referrerPolicy="no-referrer"
              className="size-8 rounded-full object-cover"
            />
          ) : (
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
              {initials(user?.displayName ?? user?.email ?? "MN")}
            </span>
          )}
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">
                  {user?.displayName ?? "Master Notes user"}
                </p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
              </div>
              <button
                onClick={() => void signOut()}
                className="rounded-lg p-2 text-slate-400 hover:bg-white hover:text-red-600"
                aria-label="Sign out"
              >
                <LogOut className="size-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden border-r border-slate-200 bg-slate-100/90 backdrop-blur lg:block",
          collapsed ? "w-16" : "w-64",
        )}
      >
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-slate-950/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <aside className="relative h-full w-72 bg-slate-100 shadow-2xl">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-3 top-3 z-10"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X className="size-5" />
            </Button>
            {sidebar}
          </aside>
        </div>
      )}

      <div
        className={cn(
          "transition-[padding] duration-200",
          collapsed ? "lg:pl-16" : "lg:pl-64",
        )}
      >
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/85 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="size-5" />
            </Button>
            {collapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex"
                onClick={() => setCollapsed(false)}
                aria-label="Expand sidebar"
              >
                <Menu className="size-5" />
              </Button>
            )}
            <HeaderSearch />
          </div>
          <Button variant="ghost" size="icon" className="relative" asChild>
            <NavLink
              to="/app/invitations"
              aria-label={
                pendingInvitations.length
                  ? `${pendingInvitations.length} pending invitation${
                      pendingInvitations.length === 1 ? "" : "s"
                    }`
                  : "Notifications"
              }
            >
              <Bell className="size-5" />
              {pendingInvitations.length > 0 && (
                <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-indigo-600 px-1 text-[10px] font-bold leading-4 text-white">
                  {pendingInvitations.length > 9 ? "9+" : pendingInvitations.length}
                </span>
              )}
            </NavLink>
          </Button>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
